import { doc, getDoc, setDoc } from 'firebase/firestore';
import * as OTPAuth from 'otpauth';
import { db } from '../config/firebase';

const MFA_COLLECTION = 'admin_mfa';
const MFA_SESSION_KEY = 'zt-admin-mfa-verified';

export interface AdminMfaRecord {
  secret: string;
  enabled: boolean;
  enrolledAt: string;
}

export interface MfaEnrollmentPayload {
  secretBase32: string;
  otpauthUri: string;
}

export const getMfaSessionKey = () => MFA_SESSION_KEY;

export const isMfaSessionVerified = (uid: string): boolean =>
  sessionStorage.getItem(MFA_SESSION_KEY) === uid;

export const markMfaSessionVerified = (uid: string) => {
  sessionStorage.setItem(MFA_SESSION_KEY, uid);
};

export const clearMfaSession = () => {
  sessionStorage.removeItem(MFA_SESSION_KEY);
};

export const createMfaEnrollment = (email: string): MfaEnrollmentPayload => {
  const secret = new OTPAuth.Secret({ size: 20 });
  const totp = new OTPAuth.TOTP({
    issuer: 'Zamaninda Tuket Admin',
    label: email,
    algorithm: 'SHA1',
    digits: 6,
    period: 30,
    secret,
  });

  return {
    secretBase32: secret.base32,
    otpauthUri: totp.toString(),
  };
};

export const verifyTotpCode = (secretBase32: string, code: string): boolean => {
  const totp = new OTPAuth.TOTP({
    algorithm: 'SHA1',
    digits: 6,
    period: 30,
    secret: OTPAuth.Secret.fromBase32(secretBase32),
  });

  const delta = totp.validate({ token: code, window: 1 });
  return delta !== null;
};

export const getAdminMfaRecord = async (uid: string): Promise<AdminMfaRecord | null> => {
  const snapshot = await getDoc(doc(db, MFA_COLLECTION, uid));
  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data() as AdminMfaRecord;
  return data.enabled ? data : null;
};

export const isAdminMfaEnrolled = async (uid: string): Promise<boolean> => {
  const record = await getAdminMfaRecord(uid);
  return record !== null;
};

export const saveAdminMfaRecord = async (uid: string, secretBase32: string) => {
  const record: AdminMfaRecord = {
    secret: secretBase32,
    enabled: true,
    enrolledAt: new Date().toISOString(),
  };

  await setDoc(doc(db, MFA_COLLECTION, uid), record);
};

export const verifyAdminMfaCode = async (uid: string, code: string): Promise<boolean> => {
  const record = await getAdminMfaRecord(uid);
  if (!record) {
    return false;
  }

  return verifyTotpCode(record.secret, code);
};
